import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ImageBlockUploader } from "./ImageBlockUploader";
import CustomModal from "../../../common/customModal/CustomModal";

// Register once at module level — Quill strips unknown tags during paste/convert,
// so we need a real blot that owns <div class="ql-table-embed"> and stores the
// raw table HTML as its value.
const BlockEmbed = Quill.import("blots/block/embed");
class TableEmbedBlot extends BlockEmbed {
  static create(html) {
    const node = super.create();
    node.innerHTML = html;
    return node;
  }
  static value(domNode) {
    return domNode.innerHTML;
  }
}
TableEmbedBlot.blotName = "tableEmbed";
TableEmbedBlot.tagName = "div";
TableEmbedBlot.className = "ql-table-embed";
Quill.register(TableEmbedBlot, true);

// ─── helpers ────────────────────────────────────────────────────────────────

const TOOLBAR_ID = "rte-custom-toolbar";

function parseInline(text) {
  return text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
}

function isMarkdownTable(text) {
  const lines = text.trim().split("\n").filter((l) => l.trim());
  return (
    lines.length >= 2 &&
    lines[0].trim().startsWith("|") &&
    /^\|[-|\s:]+\|?$/.test(lines[1].trim())
  );
}

function markdownTableToHtml(text) {
  const lines = text.trim().split("\n").filter((l) => l.trim());
  const isSep = (l) => /^\|[-|\s:]+\|?$/.test(l.trim());
  const parseRow = (l) =>
    l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());

  const [headerLine, , ...bodyLines] = lines;

  const headerCells = parseRow(headerLine)
    .map(
      (c) =>
        `<th style="border:1px solid #d1d5db;padding:6px 10px;background:#f3f4f6;font-weight:600;text-align:left">${parseInline(c)}</th>`
    )
    .join("");

  const bodyRows = bodyLines
    .filter((l) => !isSep(l))
    .map((l) => {
      const cells = parseRow(l)
        .map(
          (c) =>
            `<td style="border:1px solid #d1d5db;padding:6px 10px">${parseInline(c)}</td>`
        )
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  return `<table style="border-collapse:collapse;width:100%;margin:8px 0"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`;
}

function buildEmptyTableHtml(rows, cols) {
  const cell = `<td style="border:1px solid #d1d5db;padding:6px 10px;min-width:60px">&nbsp;</td>`;
  const row = `<tr>${cell.repeat(cols)}</tr>`;
  return `<table style="border-collapse:collapse;width:100%;margin:8px 0">${row.repeat(rows)}</table>`;
}

// ─── toolbar ────────────────────────────────────────────────────────────────

function CustomToolbar() {
  return (
    <div id={TOOLBAR_ID}>
      <span className="ql-formats">
        <select className="ql-header" defaultValue="">
          <option value="2" />
          <option value="3" />
          <option value="4" />
          <option value="" />
        </select>
      </span>
      <span className="ql-formats">
        <button className="ql-bold" />
        <button className="ql-italic" />
        <button className="ql-underline" />
        <button className="ql-strike" />
      </span>
      <span className="ql-formats">
        <button className="ql-blockquote" />
        <button className="ql-code-block" />
      </span>
      <span className="ql-formats">
        <button className="ql-list" value="ordered" />
        <button className="ql-list" value="bullet" />
      </span>
      <span className="ql-formats">
        <button className="ql-link" />
        <button className="ql-image" />
        <button className="ql-table" title="Insert table">
          <svg viewBox="0 0 18 18">
            <rect className="ql-stroke" x="2" y="2" width="14" height="14" rx="1" />
            <line className="ql-stroke" x1="2" y1="7" x2="16" y2="7" />
            <line className="ql-stroke" x1="2" y1="12" x2="16" y2="12" />
            <line className="ql-stroke" x1="7" y1="2" x2="7" y2="16" />
            <line className="ql-stroke" x1="12" y1="2" x2="12" y2="16" />
          </svg>
        </button>
      </span>
      <span className="ql-formats">
        <button className="ql-clean" />
      </span>
    </div>
  );
}

// ─── component ──────────────────────────────────────────────────────────────

export const RichTextEditor = forwardRef(function RichTextEditor(
  { value, onChange, readOnly = false, placeholder = "Start writing…" },
  ref
) {
  const quillRef = useRef(null);
  const [imgDialogOpen, setImgDialogOpen] = useState(false);
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);

  const openImgDialog = useRef(() => setImgDialogOpen(true));
  const openTableDialog = useRef(() => setTableDialogOpen(true));

  const modules = useMemo(
    () => ({
      toolbar: {
        container: `#${TOOLBAR_ID}`,
        handlers: {
          image: () => openImgDialog.current(),
          table: () => openTableDialog.current(),
        },
      },
    }),
    []
  );

  // Intercept paste in capture phase — before Quill's own handler — so we can
  // detect markdown tables and insert them as our tableEmbed blot instead of
  // letting Quill's converter strip the <table> tags.
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const handlePaste = (e) => {
      const text = e.clipboardData?.getData("text/plain");
      if (!text || !isMarkdownTable(text)) return;
      e.preventDefault();
      e.stopPropagation();
      const html = markdownTableToHtml(text);
      const range = editor.getSelection() ?? { index: editor.getLength() - 1 };
      editor.insertEmbed(range.index, "tableEmbed", html, "user");
      editor.setSelection(range.index + 1, 0, "silent");
    };

    editor.root.addEventListener("paste", handlePaste, true);
    return () => editor.root.removeEventListener("paste", handlePaste, true);
  }, []);

  const handleImageUpload = (url) => {
    const editor = quillRef.current?.getEditor();
    if (editor && url) {
      const range = editor.getSelection(true);
      editor.insertEmbed(range.index, "image", url);
    }
    setImgDialogOpen(false);
  };

  const handleInsertTable = () => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertEmbed(
        range.index,
        "tableEmbed",
        buildEmptyTableHtml(tableRows, tableCols),
        "user"
      );
      editor.setSelection(range.index + 1, 0, "silent");
    }
    setTableDialogOpen(false);
  };

  React.useImperativeHandle(ref, () => ({
    getEditor: () => quillRef.current?.getEditor(),
  }));

  return (
    <Box
      sx={{
        "& .ql-container": { minHeight: 400, fontFamily: "Heebo" },
        "& .ql-editor": { minHeight: 400 },
        "& .ql-table-embed": { margin: "8px 0", cursor: "default" },
        "& .ql-table-embed table": { borderCollapse: "collapse", width: "100%" },
        "& .ql-table-embed td, & .ql-table-embed th": {
          border: "1px solid #d1d5db",
          padding: "6px 10px",
        },
        "& .ql-table-embed th": { background: "#f3f4f6", fontWeight: 600 },
      }}
    >
      <CustomToolbar />
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        readOnly={readOnly}
        placeholder={placeholder}
        modules={modules}
      />

      <CustomModal open={imgDialogOpen} onClose={() => setImgDialogOpen(false)}>
        <Box sx={{ p: 1 }}>
          <ImageBlockUploader
            label="Upload & Insert"
            onUpload={(url) => handleImageUpload(url)}
          />
        </Box>
      </CustomModal>

      <CustomModal
        open={tableDialogOpen}
        onClose={() => setTableDialogOpen(false)}
      >
        <Box sx={{ p: 2, minWidth: 240 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontFamily: "Heebo", fontWeight: 600, mb: 2 }}
          >
            Insert Table
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Rows"
              type="number"
              size="small"
              value={tableRows}
              onChange={(e) =>
                setTableRows(Math.max(1, Math.min(20, Number(e.target.value))))
              }
              inputProps={{ min: 1, max: 20 }}
            />
            <TextField
              label="Columns"
              type="number"
              size="small"
              value={tableCols}
              onChange={(e) =>
                setTableCols(Math.max(1, Math.min(10, Number(e.target.value))))
              }
              inputProps={{ min: 1, max: 10 }}
            />
            <Button
              onClick={handleInsertTable}
              variant="contained"
              sx={{
                background:
                  "linear-gradient(180deg, #065F46 0%, #0D7B5C 100%)",
                color: "#fff",
                borderRadius: "29px",
                fontFamily: "Heebo",
                "&:hover": { opacity: 0.9 },
              }}
            >
              Insert
            </Button>
          </Stack>
        </Box>
      </CustomModal>
    </Box>
  );
});
